import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { getSessionRecommendation, getSessionScore, getRecommendationBadge, recommendationLabelFromScore, STRONG_HIRE_SCORE } from '../utils/hiringRecommendation';
import { campaignAPI, sessionAPI } from '../services/api';
import AlertsDashboard from '../components/AlertsDashboard';
import InterviewFunnel from '../components/analytics/InterviewFunnel';
import { USE_DEMO_DATA } from '../data/demoMode';
import {
  DUMMY_SESSIONS,
  buildRecruiterOptions,
  withCurrentRecruiterInDemoSessions,
} from '../data/dashboardDummyData';
import { DUMMY_CAMPAIGNS } from '../data/campaignsDummyData';
import './AdminDashboard.css';

/** @deprecated use USE_DEMO_DATA — kept so existing toggles still compile if referenced */
const USE_DASHBOARD_DUMMY_DATA = USE_DEMO_DATA;

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

function toIsoDate(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function sessionDateKey(s) {
  const raw = s.started_at || s.completed_at || s.created_at || s.invite_sent_at;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return toIsoDate(d);
}

const statusLabel = (status) => {
  const s = String(status || 'scheduled').toLowerCase();
  if (s === 'completed') return 'Interview Done';
  if (s === 'in_progress') return 'In Progress';
  if (s === 'terminated') return 'Terminated';
  return 'Invited';
};

const recLabel = (rec) => getSessionRecommendation({ hiring_recommendation: rec }).label || '';

/** Map admin session list row → dashboard queue row (matches Interviews / Candidates pages). */
function mapAdminSessionToQueueRow(row) {
  const status = String(row.status || 'scheduled').toLowerCase();
  const sec = row.duration_seconds;
  let duration = '0';
  if (status === 'in_progress' && (sec == null || Number(sec) === 0)) {
    duration = '—';
  } else if (sec != null && Number(sec) > 0) {
    duration = String(Math.round((Number(sec) / 60) * 10) / 10);
  }
  const score = row.overall_score != null ? Number(row.overall_score) : 0;
  const violations = Number(row.total_violations ?? row.violations ?? 0);
  const { key: recommendation } = getSessionRecommendation(row);
  return {
    id: String(row.id),
    candidate: row.candidate_name || '—',
    email: row.candidate_email || null,
    campaign: row.campaign_name || '—',
    campaignId: row.campaign_id != null ? String(row.campaign_id) : '',
    role: row.job_role || '—',
    recruiter: (row.recruiter_name || '').trim(),
    score: Number.isFinite(score) ? score : 0,
    violations: Number.isFinite(violations) ? violations : 0,
    duration,
    status,
    recommendation,
    started: row.started_at || null,
  };
}

const C = {
  t1: '#0F172A',
  t4: '#9CA3AF',
  border: '#E2E8F0',
};

function Pill({ label, type }) {
  const map = {
    spanish: ['#EEF2FF', '#4338CA'],
    english: ['#F0F9FF', '#0369A1'],
  };
  const [bg, fg] = map[type] || map.english;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: bg, color: fg }}>
      {label}
    </span>
  );
}

/** Stroke icons aligned with AdminLayout sidebar (viewBox, strokeWidth, line caps). */
function KpiLineIcon({ children, color = '#94A3B8' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
      style={{ flexShrink: 0, display: 'block', opacity: 0.85 }}
      aria-hidden
    >
      {children}
    </svg>
  );
}

const KPI_ICONS = {
  campaigns: (color) => (
    <KpiLineIcon color={color}>
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </KpiLineIcon>
  ),
  interviews: (color) => (
    <KpiLineIcon color={color}>
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </KpiLineIcon>
  ),
  review: (color) => (
    <KpiLineIcon color={color}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </KpiLineIcon>
  ),
  hire: (color) => (
    <KpiLineIcon color={color}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </KpiLineIcon>
  ),
  flagged: (color) => (
    <KpiLineIcon color={color}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </KpiLineIcon>
  ),
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [panelCandidate, setPanelCandidate] = useState(null);
  const [kpiPopup, setKpiPopup] = useState(null);
  const [exporting, setExporting] = useState(false);

  const todayMaxDate = useMemo(() => toIsoDate(new Date()), []);
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toIsoDate(d);
  });
  const [to, setTo] = useState(() => toIsoDate(new Date()));
  const [selectedPositions, setSelectedPositions] = useState(() => new Set());
  const [selectedRecruiters, setSelectedRecruiters] = useState(() => new Set());
  const [positionOpen, setPositionOpen] = useState(false);
  const [recruiterOpen, setRecruiterOpen] = useState(false);
  const positionRef = useRef(null);
  const recruiterRef = useRef(null);

  const fetchDashboard = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      if (USE_DASHBOARD_DUMMY_DATA) {
        setAllCampaigns(DUMMY_CAMPAIGNS);
        setAllSessions(withCurrentRecruiterInDemoSessions(DUMMY_SESSIONS));
        return;
      }
      const [campaignsRes, sessionsRes] = await Promise.all([
        campaignAPI.listForStats(true),
        sessionAPI.getAllSessions({ scope: 'stats' }),
      ]);
      setAllCampaigns(campaignsRes.data || []);
      setAllSessions(sessionsRes.data || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      setAllCampaigns([]);
      setAllSessions([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboard(false);
  }, [fetchDashboard]);

  useEffect(() => {
    if (!positionOpen) return;
    const handler = (e) => {
      if (positionRef.current && !positionRef.current.contains(e.target)) setPositionOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [positionOpen]);

  useEffect(() => {
    if (!recruiterOpen) return;
    const handler = (e) => {
      if (recruiterRef.current && !recruiterRef.current.contains(e.target)) setRecruiterOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [recruiterOpen]);

  const positionOptions = useMemo(() => {
    const map = new Map();
    (allCampaigns || []).forEach((c) => {
      const id = String(c.id);
      map.set(id, {
        id,
        name: c.name || 'Unknown',
        role: c.job_role || '',
        isActive: !!c.is_active,
      });
    });
    (allSessions || []).forEach((s) => {
      const id = String(s.campaign_id || '');
      if (!id || map.has(id)) return;
      map.set(id, {
        id,
        name: s.campaign_name || 'Unknown',
        role: s.job_role || '',
        isActive: true,
      });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allCampaigns, allSessions]);

  const recruiterOptions = useMemo(
    () => buildRecruiterOptions(allSessions),
    [allSessions]
  );

  const dateRangeError = useMemo(() => {
    if (!from || !to) return 'Please select both From and To dates.';
    if (from > to) return 'To date must be on or after the From date.';
    if (to > todayMaxDate) return 'To date cannot be in the future.';
    return '';
  }, [from, to, todayMaxDate]);

  const filteredSessions = useMemo(() => {
    if (dateRangeError) return [];
    return (allSessions || []).filter((s) => {
      const key = sessionDateKey(s);
      if (key) {
        if (from && key < from) return false;
        if (to && key > to) return false;
      } else if (from || to) {
        return false;
      }
      if (selectedPositions.size > 0) {
        const cid = String(s.campaign_id || s.campaign_name || '');
        if (!selectedPositions.has(cid)) return false;
      }
      if (selectedRecruiters.size > 0) {
        const rec = (s.recruiter_name || '').trim();
        if (!selectedRecruiters.has(rec)) return false;
      }
      return true;
    });
  }, [allSessions, from, to, selectedPositions, selectedRecruiters, dateRangeError]);

  const queueSessions = useMemo(
    () => filteredSessions.slice(0, 20).map(mapAdminSessionToQueueRow),
    [filteredSessions]
  );
  const queueCandidates = useMemo(
    () =>
      queueSessions.map((r) => ({
        id: r.id,
        name: r.candidate,
        email: r.email,
        recommendation: r.recommendation,
      })),
    [queueSessions]
  );
  const queueLoading = loading;

  const applyPreset = (days) => {
    const t = new Date();
    const f = new Date();
    if (days > 0) f.setDate(f.getDate() - days);
    setFrom(toIsoDate(f));
    setTo(toIsoDate(t));
  };

  const activePresetDays = useMemo(() => {
    if (!from || !to) return null;
    const today = toIsoDate(new Date());
    if (to !== today) return null;
    const fromDate = new Date(`${from}T00:00:00`);
    const toDate = new Date(`${to}T00:00:00`);
    const diffDays = Math.round((toDate - fromDate) / 86400000);
    if (diffDays === 0) return 0;
    if (diffDays === 7) return 7;
    if (diffDays === 30) return 30;
    return null;
  }, [from, to]);

  const datePresets = [
    { label: 'Today', days: 0 },
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
  ];

  const togglePosition = (id) => {
    setSelectedPositions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleRecruiter = (name) => {
    setSelectedRecruiters((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const removePositionFilter = (id) => {
    setSelectedPositions((prev) => {
      const next = new Set(prev);
      next.delete(String(id));
      return next;
    });
  };

  const removeRecruiterFilter = (name) => {
    setSelectedRecruiters((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const positionLabel =
    selectedPositions.size === 0
      ? 'All roles'
      : selectedPositions.size === 1
        ? positionOptions.find((p) => selectedPositions.has(p.id))?.name || '1 selected'
        : `${selectedPositions.size} roles`;

  const recruiterLabel =
    selectedRecruiters.size === 0
      ? 'All Users'
      : selectedRecruiters.size === 1
        ? Array.from(selectedRecruiters)[0]
        : `${selectedRecruiters.size} users`;

  const displayPeriod =
    from && to
      ? `${from.split('-').reverse().join('/')} → ${to.split('-').reverse().join('/')}`
      : '';

  const filterInp = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '9px 12px',
    borderRadius: 8,
    border: '1.5px solid #E2E8F0',
    background: '#fff',
    fontSize: 13,
    fontFamily: 'inherit',
    color: '#0F172A',
    outline: 'none',
  };

  const filterLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    color: '#64748B',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  };

  const chipBase = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 20,
    padding: '4px 10px',
    fontSize: 11,
    fontWeight: 600,
    maxWidth: 280,
  };

  const chipClearBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: 0,
    margin: 0,
    opacity: 0.75,
    flexShrink: 0,
    fontFamily: 'inherit',
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = filteredSessions;
      const rows = data.map((c) => {
        const score = c.overall_score;
        return {
          Name: c.candidate_name ?? '',
          Email: c.candidate_email ?? '',
          Position: c.campaign_name ?? '',
          Role: c.job_role ?? '',
          Recruiter: c.recruiter_name ?? '',
          'Invited Date': fmtDate(c.invite_sent_at ?? c.created_at),
          Status: statusLabel(c.status),
          'Completed Date': fmtDate(c.completed_at),
          'Overall Score': score != null && score !== '' ? Number(score).toFixed(1) : '',
          Recommendation: getSessionRecommendation(c).label,
        };
      });
      if (rows.length === 0) {
        alert('There are no candidates to export for the current filters.');
        return;
      }
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 22 }, { wch: 30 }, { wch: 28 }, { wch: 22 }, { wch: 20 }, { wch: 14 },
        { wch: 15 }, { wch: 15 }, { wch: 13 }, { wch: 16 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Candidates');
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `wizetok-candidates-${stamp}.xlsx`);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const activePositionRows = useMemo(() => {
    let list = (positionOptions || []).filter((p) => p.isActive);
    if (selectedPositions.size > 0) {
      list = list.filter((p) => selectedPositions.has(p.id));
    } else if (selectedRecruiters.size > 0) {
      const ids = new Set(
        (filteredSessions || []).map((s) => String(s.campaign_id || '')).filter(Boolean)
      );
      list = list.filter((p) => ids.has(p.id));
    }
    return list.map((p) => {
      const related = (filteredSessions || []).filter((s) => String(s.campaign_id || '') === String(p.id));
      return {
        id: p.id,
        name: p.name,
        role: p.role || '—',
        status: 'Active',
        candidates: related.length,
      };
    });
  }, [positionOptions, selectedPositions, selectedRecruiters, filteredSessions]);

  if (loading) {
    return (
      <div className="admin-dashboard" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const T = {
    bg: '#F8FAFC',
    card: '#FFFFFF',
    bdr: '#E2E8F0',
    t0: '#0F172A',
    t1: '#334155',
    t2: '#475569',
    t3: '#94A3B8',
    blue: '#3B82F6',
    blueLight: '#EFF6FF',
    purple: '#8B5CF6',
    purpleLight: '#F5F3FF',
    amber: '#F59E0B',
    amberLight: '#FEF3C7',
    green: '#10B981',
    greenLight: '#D1FAE5',
    red: '#EF4444',
    redLight: '#FEE2E2',
    brand: '#6366F1',
    brandLight: '#EEF2FF',
  };

  const openKpiPopup = (card) => {
    setKpiPopup({
      title: card.label,
      color: card.color,
      subtitle: card.popupSubtitle || '',
      kind: card.kind || 'sessions',
      rows: card.rows || [],
      ranked: !!card.ranked,
      hideRecommendation: !!card.hideRecommendation,
      showViolations: !!card.showViolations,
    });
  };

  // KPIs from filtered sessions + campaigns
  const filteredCampaignIds = new Set(
    filteredSessions.map((s) => String(s.campaign_id || '')).filter(Boolean)
  );
  const activePositions =
    selectedPositions.size > 0
      ? positionOptions.filter((p) => selectedPositions.has(p.id) && p.isActive).length
      : selectedRecruiters.size > 0
        ? positionOptions.filter((p) => filteredCampaignIds.has(p.id) && p.isActive).length
        : positionOptions.filter((p) => p.isActive).length;

  const inProgress = filteredSessions.filter((s) => String(s.status || '').toLowerCase() === 'in_progress');
  const awaitingRev = filteredSessions.filter((s) => String(s.status || '').toLowerCase() === 'completed');
  const strongHire = filteredSessions.filter(
    (s) => getSessionRecommendation(s).key === 'strong_yes'
  );
  const flagged = filteredSessions.filter(
    (s) => Number(s.total_violations ?? s.violations ?? 0) > 10
  );

  const KPI_CARDS = [
    {
      label: 'Active Positions',
      value: activePositions,
      sub: displayPeriod || 'currently running',
      color: T.blue,
      icon: KPI_ICONS.campaigns,
      kind: 'positions',
      rows: activePositionRows,
      popupSubtitle: 'Currently active hiring positions',
    },
    {
      label: 'Interviews',
      value: filteredSessions.length,
      sub: `${inProgress.length} in progress`,
      color: T.purple,
      icon: KPI_ICONS.interviews,
      rows: filteredSessions,
      popupSubtitle: displayPeriod || 'All interviews in selected range',
    },
    {
      label: 'Awaiting Review',
      value: awaitingRev.length,
      sub: 'completed, not actioned',
      color: T.amber,
      icon: KPI_ICONS.review,
      rows: awaitingRev,
      popupSubtitle: 'Completed interviews awaiting action',
    },
    {
      label: 'Strong Hire',
      value: strongHire.length,
      sub: 'across filtered positions',
      color: T.green,
      icon: KPI_ICONS.hire,
      rows: strongHire,
      ranked: true,
      popupSubtitle: `Score ≥ ${STRONG_HIRE_SCORE} · ranked by score`,
    },
    {
      label: 'Flagged Candidates',
      value: flagged.length,
      sub: 'high violations',
      color: T.red,
      icon: KPI_ICONS.flagged,
      rows: flagged,
      showViolations: true,
      hideRecommendation: false,
      popupSubtitle: 'More than 10 proctoring violations',
    },
  ];

  const SC = (score) => {
    const n = Number(score);
    if (Number.isNaN(n)) return T.t3;
    if (n >= 8) return T.green;
    if (n >= 6) return T.amber;
    return T.red;
  };

  return (
    <div className="page-anim" style={{ flex: 1, overflowY: 'auto', padding: '28px 32px', background: T.bg }}>
      {/* Page header + filter chips */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.t0 }}>Dashboard</div>
          {Array.from(selectedPositions).map((id) => {
            const pos = positionOptions.find((p) => p.id === String(id));
            return (
              <span
                key={`pos-${id}`}
                style={{
                  ...chipBase,
                  background: 'rgba(59,130,246,0.12)',
                  border: '1px solid rgba(59,130,246,0.25)',
                  color: '#2563EB',
                }}
                title={pos?.name || id}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {pos?.name || `Position ${id}`}
                </span>
                <button
                  type="button"
                  onClick={() => removePositionFilter(id)}
                  title="Remove position filter"
                  style={{ ...chipClearBtn, color: '#2563EB' }}
                >
                  ✕
                </button>
              </span>
            );
          })}
          {Array.from(selectedRecruiters).map((name) => (
            <span
              key={`rec-${name}`}
              style={{
                ...chipBase,
                background: 'rgba(16,185,129,0.12)',
                border: '1px solid rgba(16,185,129,0.3)',
                color: '#059669',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
              <button
                type="button"
                onClick={() => removeRecruiterFilter(name)}
                title="Remove user filter"
                style={{ ...chipClearBtn, color: '#059669' }}
              >
                ✕
              </button>
            </span>
          ))}
          {(selectedPositions.size > 0 || selectedRecruiters.size > 0) && (
            <button
              type="button"
              onClick={() => {
                setSelectedPositions(new Set());
                setSelectedRecruiters(new Set());
              }}
              title="Clear all filters"
              style={{
                ...chipBase,
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                color: '#DC2626',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Clear
            </button>
          )}
          {selectedPositions.size === 0 && selectedRecruiters.size === 0 && (
            <span
              style={{
                background: '#F1F5F9',
                border: '1px solid #E2E8F0',
                color: '#64748B',
                borderRadius: 20,
                padding: '3px 10px',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              All roles
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: T.t2, marginBottom: 16 }}>
          Quick view of usage + outcomes for your chosen date range.
        </div>
      </div>

      {/* Filters card */}
      <div
        style={{
          background: T.card,
          border: `1px solid ${T.bdr}`,
          borderRadius: 14,
          padding: 20,
          marginBottom: 20,
          boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 200px', minWidth: 170 }} ref={positionRef}>
            <div style={filterLabelStyle}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              Role / Position
            </div>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => { setPositionOpen((o) => !o); setRecruiterOpen(false); }}
                style={{
                  ...filterInp,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  textAlign: 'left',
                  borderColor: selectedPositions.size > 0 ? 'rgba(37,99,235,0.5)' : '#E2E8F0',
                  background: selectedPositions.size > 0 ? 'rgba(37,99,235,0.07)' : '#fff',
                }}
              >
                <span style={{ fontSize: 13, color: selectedPositions.size > 0 ? '#2563EB' : '#64748B', fontWeight: selectedPositions.size > 0 ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {positionLabel}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" style={{ flexShrink: 0, transform: positionOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s' }}><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {positionOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40, background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '100%', maxWidth: 340, maxHeight: 240, overflowY: 'auto' }}>
                  {selectedPositions.size > 0 && (
                    <button type="button" onClick={() => setSelectedPositions(new Set())} style={{ display: 'block', width: '100%', padding: '8px 14px', background: 'transparent', border: 'none', borderBottom: '1px solid #E2E8F0', cursor: 'pointer', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#EF4444', fontFamily: 'inherit' }}>
                      ✕ Clear selection
                    </button>
                  )}
                  {positionOptions.length === 0 ? (
                    <div style={{ padding: '12px 14px', color: '#94A3B8', fontSize: 12 }}>No positions found</div>
                  ) : positionOptions.map((p) => {
                    const checked = selectedPositions.has(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => togglePosition(p.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 14px', background: checked ? 'rgba(37,99,235,0.08)' : 'transparent', border: 'none', borderBottom: '1px solid #F1F5F9', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
                      >
                        <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${checked ? '#2563EB' : '#CBD5E1'}`, background: checked ? '#2563EB' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {checked && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#0F172A', lineHeight: 1.3 }}>{p.name}</div>
                          {p.role ? <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 1 }}>{p.role}</div> : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div style={{ flex: '1 1 200px', minWidth: 170 }} ref={recruiterRef}>
            <div style={filterLabelStyle}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              User (SSO)
            </div>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => { setRecruiterOpen((o) => !o); setPositionOpen(false); }}
                style={{
                  ...filterInp,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  textAlign: 'left',
                  borderColor: selectedRecruiters.size > 0 ? 'rgba(16,185,129,0.5)' : '#E2E8F0',
                  background: selectedRecruiters.size > 0 ? 'rgba(16,185,129,0.07)' : '#fff',
                }}
              >
                <span style={{ fontSize: 13, color: selectedRecruiters.size > 0 ? '#059669' : '#64748B', fontWeight: selectedRecruiters.size > 0 ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {recruiterLabel}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" style={{ flexShrink: 0, transform: recruiterOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s' }}><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {recruiterOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40, background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '100%', maxWidth: 300, maxHeight: 240, overflowY: 'auto' }}>
                  {selectedRecruiters.size > 0 && (
                    <button type="button" onClick={() => setSelectedRecruiters(new Set())} style={{ display: 'block', width: '100%', padding: '8px 14px', background: 'transparent', border: 'none', borderBottom: '1px solid #E2E8F0', cursor: 'pointer', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#EF4444', fontFamily: 'inherit' }}>
                      ✕ Clear selection
                    </button>
                  )}
                  {recruiterOptions.length === 0 ? (
                    <div style={{ padding: '12px 14px', color: '#94A3B8', fontSize: 12 }}>No recruiters found</div>
                  ) : recruiterOptions.map((name) => {
                    const checked = selectedRecruiters.has(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => toggleRecruiter(name)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 14px', background: checked ? 'rgba(16,185,129,0.08)' : 'transparent', border: 'none', borderBottom: '1px solid #F1F5F9', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
                      >
                        <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${checked ? '#059669' : '#CBD5E1'}`, background: checked ? '#059669' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {checked && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#0F172A' }}>{name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div style={{ flex: '1 1 160px', minWidth: 140 }}>
            <div style={filterLabelStyle}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              From
            </div>
            <input
              type="date"
              value={from}
              max={to || todayMaxDate}
              onChange={(e) => setFrom(e.target.value)}
              style={{ ...filterInp, borderColor: dateRangeError ? '#EF4444' : '#E2E8F0' }}
            />
          </div>

          <div style={{ flex: '1 1 160px', minWidth: 140 }}>
            <div style={filterLabelStyle}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              To
            </div>
            <input
              type="date"
              value={to}
              min={from || undefined}
              max={todayMaxDate}
              onChange={(e) => setTo(e.target.value)}
              style={{ ...filterInp, borderColor: dateRangeError ? '#EF4444' : '#E2E8F0' }}
            />
          </div>

          <div style={{ flex: '0 0 130px', minWidth: 120, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              type="button"
              onClick={() => fetchDashboard(true)}
              disabled={refreshing || !!dateRangeError}
              style={{
                width: '100%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '10px 14px',
                borderRadius: 8,
                border: 'none',
                background: '#2563EB',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: refreshing || dateRangeError ? 'not-allowed' : 'pointer',
                opacity: refreshing || dateRangeError ? 0.6 : 1,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
              {refreshing ? 'Loading…' : 'Refresh'}
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting || !!dateRangeError}
              style={{
                width: '100%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 8,
                border: '1.5px solid rgba(37,99,235,0.35)',
                background: 'rgba(37,99,235,0.07)',
                color: '#1D4ED8',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: exporting || dateRangeError ? 'not-allowed' : 'pointer',
                opacity: exporting || dateRangeError ? 0.5 : 1,
              }}
            >
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {exporting ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 10,
            marginTop: 14,
            width: '100%',
          }}
        >
          {datePresets.map((p) => {
            const active = activePresetDays === p.days;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPreset(p.days)}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: active ? '2px solid #2563EB' : '1.5px solid #E2E8F0',
                  background: active ? 'rgba(37,99,235,0.08)' : '#fff',
                  color: active ? '#1D4ED8' : '#334155',
                  fontSize: 13,
                  fontWeight: active ? 700 : 600,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'border-color .15s, background .15s, color .15s',
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {dateRangeError && (
          <div style={{ marginTop: 14, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '10px 14px', color: '#D97706', fontSize: 13 }}>
            {dateRangeError}
          </div>
        )}
      </div>

      {/* ── SECTION 1: KPI CARDS ── */}
      <div className="stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 24 }}>
        {KPI_CARDS.map((k) => (
          <div
            key={k.label}
            role="button"
            tabIndex={0}
            title={`View ${k.label}`}
            onClick={() => openKpiPopup(k)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openKpiPopup(k);
              }
            }}
            style={{
              background: T.card,
              borderRadius: 14,
              padding: '18px 20px',
              border: `1px solid ${T.bdr}`,
              borderTop: `3px solid ${k.color}`,
              transition: 'all .2s',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 8px 24px ${k.color}18`;
              e.currentTarget.style.borderColor = `${k.color}55`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = T.bdr;
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.t3, textTransform: 'uppercase', letterSpacing: 1.2 }}>{k.label}</div>
              {typeof k.icon === 'function' ? k.icon(k.color) : k.icon}
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: k.color, lineHeight: 1, marginBottom: 6 }}>{k.value}</div>
            <div style={{ fontSize: 11, color: T.t3 }}>{k.sub}</div>
          </div>
        ))}
          </div>

      {kpiPopup && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            background: 'rgba(15,23,42,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setKpiPopup(null);
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 14,
              width: 'min(920px, 96vw)',
              maxHeight: '88vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 50px rgba(15,23,42,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: '1px solid #E2E8F0',
                gap: 12,
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: kpiPopup.color, display: 'inline-block' }} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#0F172A' }}>{kpiPopup.title}</span>
                </div>
                <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                  {kpiPopup.rows.length}{' '}
                  {kpiPopup.kind === 'positions'
                    ? `position${kpiPopup.rows.length === 1 ? '' : 's'}`
                    : `candidate${kpiPopup.rows.length === 1 ? '' : 's'}`}
                  {kpiPopup.subtitle ? ` · ${kpiPopup.subtitle}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setKpiPopup(null)}
                style={{
                  border: 'none',
                  background: '#F1F5F9',
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 16,
                  color: '#64748B',
                  fontFamily: 'inherit',
                }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {kpiPopup.rows.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
                  {kpiPopup.kind === 'positions' ? 'No positions in this view.' : 'No candidates in this view.'}
                </div>
              ) : kpiPopup.kind === 'positions' ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Position', 'Role', 'Status', 'Interviews'].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: 'left',
                            padding: '10px 14px',
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#64748B',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            borderBottom: '1px solid #E2E8F0',
                            background: '#F8FAFC',
                            position: 'sticky',
                            top: 0,
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {kpiPopup.rows.map((p) => (
                      <tr key={p.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ padding: '11px 14px', fontWeight: 600, color: '#0F172A', fontSize: 13 }}>{p.name}</td>
                        <td style={{ padding: '11px 14px', color: '#64748B', fontSize: 12 }}>{p.role}</td>
                        <td style={{ padding: '11px 14px', fontSize: 12, fontWeight: 700, color: '#16A34A' }}>{p.status}</td>
                        <td style={{ padding: '11px 14px', fontWeight: 700, fontSize: 13, color: '#0F172A' }}>{p.candidates}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {(kpiPopup.ranked
                        ? kpiPopup.hideRecommendation
                          ? ['Rank', 'Candidate', 'Email', 'Position', 'Score']
                          : ['Rank', 'Candidate', 'Email', 'Position', 'Score', 'Recommendation']
                        : kpiPopup.showViolations
                          ? ['Candidate', 'Email', 'Position', 'Status', 'Violations', 'Score', 'Recommendation']
                          : kpiPopup.hideRecommendation
                            ? ['Candidate', 'Email', 'Position', 'Status', 'Score']
                            : ['Candidate', 'Email', 'Position', 'Status', 'Score', 'Recommendation']
                      ).map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: 'left',
                            padding: '10px 14px',
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#64748B',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            borderBottom: '1px solid #E2E8F0',
                            background: '#F8FAFC',
                            position: 'sticky',
                            top: 0,
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(kpiPopup.ranked
                      ? [...kpiPopup.rows].sort((a, b) => getSessionScore(b) - getSessionScore(a))
                      : kpiPopup.rows
                    ).map((s, idx) => {
                      const name = s.candidate_name ?? s.name ?? '—';
                      const email = s.candidate_email ?? s.email ?? '—';
                      const campaign = s.campaign_name ?? s.campaign ?? '—';
                      const status = String(s.status || 'scheduled').replace(/_/g, ' ');
                      const scoreNum = getSessionScore(s);
                      const score = scoreNum > 0 ? scoreNum.toFixed(1) : '—';
                      const viol = Number(s.total_violations ?? s.violations ?? 0);
                      const badge = getRecommendationBadge(
                        kpiPopup.ranked ? { overall_score: scoreNum } : s
                      );
                      const recLabel = kpiPopup.ranked
                        ? recommendationLabelFromScore(scoreNum) ?? badge.label
                        : badge.label;
                      return (
                        <tr key={s.id || `${email}-${idx}`} style={{ borderBottom: '1px solid #F1F5F9' }}>
                          {kpiPopup.ranked && (
                            <td style={{ padding: '11px 14px', fontWeight: 700, color: '#64748B', fontSize: 12 }}>#{idx + 1}</td>
                          )}
                          <td style={{ padding: '11px 14px', fontWeight: 600, color: '#0F172A', fontSize: 13 }}>{name}</td>
                          <td style={{ padding: '11px 14px', color: '#64748B', fontSize: 12 }}>{email}</td>
                          <td style={{ padding: '11px 14px', color: '#334155', fontSize: 12 }}>{campaign}</td>
                          {!kpiPopup.ranked && (
                            <td style={{ padding: '11px 14px', fontSize: 12, textTransform: 'capitalize', color: '#64748B' }}>
                              {status}
                            </td>
                          )}
                          {kpiPopup.showViolations && (
                            <td style={{ padding: '11px 14px', fontWeight: 700, fontSize: 13, color: '#DC2626' }}>{viol}</td>
                          )}
                          <td style={{ padding: '11px 14px', fontWeight: 700, fontSize: 13, color: '#0F172A' }}>{score}</td>
                          {!kpiPopup.hideRecommendation && (
                            <td style={{ padding: '11px 14px' }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  padding: '3px 10px',
                                  borderRadius: 20,
                                  background: badge.bg,
                                  color: badge.color,
                                }}
                              >
                                {recLabel}
                              </span>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── SECTION 2: INTERVIEW FUNNEL ── */}
      <div style={{ marginBottom: 8 }}>
        <InterviewFunnel sessions={filteredSessions} showDetails={false} />
      </div>

      {/* ── SECTION 3: ALERTS & EXCEPTIONS ── */}
      <div style={{ marginBottom: 24 }}>
        <AlertsDashboard />
          </div>

      {/* ── SECTION 3: ACTIVE CANDIDATE QUEUE ── */}
      <div style={{ background: T.card, border: `1px solid ${T.bdr}`, borderRadius: 14, overflow: 'hidden', marginBottom: 24 }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 20px',
            borderBottom: `1px solid ${T.bdr}`,
            background: T.bg,
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.t0 }}>Active candidate queue</div>
            <div style={{ fontSize: 10, color: T.t3, marginTop: 2 }}>
              In progress now + completed sessions needing your action
            </div>
          </div>
            <button 
            type="button"
            onClick={() => navigate('/admin/candidates')}
            style={{
              fontSize: 11,
              color: T.brand,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              fontFamily: 'inherit',
            }}
          >
            View all →
            </button>
          </div>

        {/* Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
              {['Candidate', 'Position', 'Score', 'Risk Level', 'Violations', 'Status', 'Actions'].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: '9px 18px',
                    fontSize: 9,
                    color: T.t3,
                    textTransform: 'uppercase',
                    letterSpacing: 1.1,
                    fontWeight: 700,
                    borderBottom: `1px solid ${T.bdr}`,
                    background: T.bg,
                    textAlign: 'left',
                  }}
                >
                  {h}
                </th>
              ))}
                  </tr>
                </thead>
                <tbody>
            {queueLoading ? (
              <tr>
                <td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: T.t3, fontSize: 12 }}>
                  Loading candidates...
                </td>
              </tr>
            ) : queueSessions.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: T.t3, fontSize: 12 }}>
                  No recent sessions found.
                </td>
              </tr>
            ) : (
              queueSessions
                .filter(
                  (s) =>
                    s.status === 'in_progress' ||
                    s.violations > 10 ||
                    (s.status === 'completed' &&
                      (s.recommendation === 'no' || s.recommendation === 'strong_no'))
                )
                .slice(0, 5)
                .map((s) => {
              const cand = queueCandidates.find((c) => c.id === s.id);
              const durationMins = s.duration === '—' ? 0 : parseFloat(s.duration) || 1;
              const rate = durationMins > 0 ? s.violations / durationMins : s.violations;
              const risk =
                rate === 0
                  ? { label: 'Low', color: T.green, bg: T.greenLight }
                  : rate < 2
                    ? { label: 'Low', color: T.green, bg: T.greenLight }
                    : rate < 5
                      ? { label: 'Medium', color: T.amber, bg: T.amberLight }
                      : rate < 10
                        ? { label: 'High', color: T.red, bg: T.redLight }
                        : { label: 'Critical', color: '#7F1D1D', bg: T.redLight };
              const initials = s.candidate
                .split(' ')
                .map((w) => w[0])
                .join('')
                .slice(0, 2);
              const avatarColors = {
                'Meera Nair': [T.greenLight, T.green],
                'Priya Sharma': [T.blueLight, T.blue],
                'Ravi Teja': [T.amberLight, T.amber],
                'Sai Kuncha': [T.redLight, T.red],
                Sai: [T.brandLight, T.brand],
                'Arjun Mehta': [T.purpleLight, T.purple],
              };
              const [avBg, avTx] = avatarColors[s.candidate] || [T.bg, T.t2];

              return (
                <tr
                  key={s.id}
                  style={{ transition: 'background .14s', cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = T.bg;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                  onClick={() => setPanelCandidate(s)}
                >
                  {/* Candidate */}
                  <td style={{ padding: '12px 18px', borderBottom: `1px solid ${T.bdr}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: avBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 700,
                          color: avTx,
                          flexShrink: 0,
                        }}
                      >
                        {initials}
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t0 }}>{s.candidate}</div>
                        <div style={{ fontSize: 10, color: T.t3 }}>{s.email || cand?.email || '—'}</div>
                      </div>
                    </div>
                  </td>

                  {/* Campaign */}
                  <td style={{ padding: '12px 18px', borderBottom: `1px solid ${T.bdr}` }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: T.t1 }}>{s.campaign}</div>
                    <div style={{ fontSize: 10, color: T.t3 }}>{s.role}</div>
                  </td>

                  {/* Score */}
                  <td style={{ padding: '12px 18px', borderBottom: `1px solid ${T.bdr}` }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: SC(s.score) }}>{Number(s.score).toFixed(1)}</span>
                  </td>

                  {/* Risk level */}
                  <td style={{ padding: '12px 18px', borderBottom: `1px solid ${T.bdr}` }}>
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        padding: '3px 9px',
                        borderRadius: 20,
                        background: risk.bg,
                        color: risk.color,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {risk.label} risk
                    </span>
                  </td>

                  {/* Violations */}
                  <td style={{ padding: '12px 18px', borderBottom: `1px solid ${T.bdr}` }}>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: s.violations === 0 ? T.green : s.violations > 10 ? T.red : T.amber,
                      }}
                    >
                      {s.violations}
                        </span>
                      </td>

                  {/* Status */}
                  <td style={{ padding: '12px 18px', borderBottom: `1px solid ${T.bdr}` }}>
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        padding: '3px 9px',
                        borderRadius: 20,
                        background: s.status === 'completed' ? T.greenLight : T.amberLight,
                        color: s.status === 'completed' ? T.green : T.amber,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s.status === 'completed' ? 'Completed' : 'In Progress'}
                    </span>
                      </td>

                  {/* Actions */}
                  <td style={{ padding: '12px 18px', borderBottom: `1px solid ${T.bdr}` }} onClick={(e) => e.stopPropagation()}>
                    {s.status === 'in_progress' ? (
                      <button
                        type="button"
                        style={{
                          fontSize: 10,
                          padding: '4px 10px',
                          borderRadius: 7,
                          border: '1px solid #0EA5E9',
                          color: '#0EA5E9',
                          background: 'none',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontWeight: 600,
                        }}
                      >
                        Watch live
                      </button>
                    ) : s.violations > 10 ? (
                      <button
                        type="button"
                        style={{
                          fontSize: 10,
                          padding: '4px 10px',
                          borderRadius: 7,
                          border: `1px solid ${T.red}`,
                          color: T.red,
                          background: 'none',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontWeight: 600,
                        }}
                      >
                        Investigate
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        <button
                          type="button"
                          onClick={() => navigate('/admin/reports')}
                          style={{
                            fontSize: 10,
                            padding: '4px 10px',
                            borderRadius: 7,
                            border: `1px solid ${T.brand}`,
                            color: T.brand,
                            background: 'none',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            fontWeight: 600,
                          }}
                        >
                          Review
                        </button>
                        <button
                          type="button"
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: 6,
                            border: `1px solid ${T.bdr}`,
                            background: 'none',
                            cursor: 'pointer',
                            fontSize: 11,
                            color: T.green,
                          }}
                        >
                          ✓
                        </button>
                        <button 
                          type="button"
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: 6,
                            border: `1px solid ${T.bdr}`,
                            background: 'none',
                            cursor: 'pointer',
                            fontSize: 11,
                            color: T.red,
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                      </td>
                    </tr>
              );
            })
          )}
                </tbody>
              </table>
            </div>

      {/* ── SECTION 4: PIPELINE SNAPSHOT ── */}
      <div style={{ background: T.card, border: `1px solid ${T.bdr}`, borderRadius: 14, padding: '22px 24px', marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.t0, marginBottom: 18 }}>Hiring pipeline snapshot</div>

        {/* Stage track */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {[
            {
              label: 'Added',
              val: filteredSessions.length,
              pct: null,
              pctC: T.t3,
            },
            {
              label: 'Invited',
              val: filteredSessions.length,
              pct: filteredSessions.length ? '100%' : '0%',
              pctC: T.green,
            },
            {
              label: 'Started',
              val: filteredSessions.filter((s) => {
                const st = String(s.status || '').toLowerCase();
                return st === 'in_progress' || st === 'completed' || st === 'terminated';
              }).length,
              pct: null,
              pctC: T.green,
            },
            {
              label: 'Completed',
              val: filteredSessions.filter((s) => String(s.status || '').toLowerCase() === 'completed').length,
              pct: null,
              pctC: T.green,
            },
            {
              label: 'Recommended',
              val: filteredSessions.filter((s) => {
                const k = getSessionRecommendation(s).key;
                return k === 'strong_yes' || k === 'yes';
              }).length,
              pct: null,
              pctC: T.red,
              warn: true,
            },
            {
              label: 'Strong Hire',
              val: filteredSessions.filter((s) => getSessionRecommendation(s).key === 'strong_yes').length,
              pct: null,
              pctC: T.green,
              ok: true,
            },
          ].map((s, i, arr) => (
            <React.Fragment key={s.label}>
              <div
                style={{ flex: 1 }}
                onMouseEnter={(e) => {
                  e.currentTarget.firstChild.style.borderColor = T.brand;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.firstChild.style.borderColor = s.warn ? '#FECACA' : s.ok ? '#BBF7D0' : T.bdr;
                }}
              >
                <div
                  style={{
                    margin: '0 5px',
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    background: s.warn ? T.redLight : s.ok ? T.greenLight : T.bg,
                    border: `1px solid ${s.warn ? '#FECACA' : s.ok ? '#BBF7D0' : T.bdr}`,
                    transition: 'border-color .2s',
                    cursor: 'default',
                  }}
                >
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '.7px',
                      color: s.warn ? T.red : T.t3,
                      marginBottom: 6,
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: s.warn ? T.red : s.ok ? T.green : T.t0,
                      lineHeight: 1,
                      letterSpacing: '-1px',
                    }}
                  >
                    {s.val}
                  </div>
                  {s.pct ? (
                    <div style={{ fontSize: 9, fontWeight: 700, marginTop: 5, color: s.pctC }}>
                      {s.pct} ↓{s.warn ? ' ▲' : ''}
                    </div>
                  ) : (
                    <div style={{ fontSize: 9, marginTop: 5, color: '#94A3B8' }}>baseline</div>
                  )}
                </div>
              </div>
              {i < arr.length - 1 && (
                <div style={{ color: '#94A3B8', fontSize: 14, flexShrink: 0, margin: '0 -2px' }}>›</div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Drop-off note */}
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '8px 14px',
            borderRadius: 9,
            background: T.redLight,
            border: '1px solid #FECACA',
            fontSize: 11,
            fontWeight: 500,
            color: T.red,
          }}
        >
          ▲ Biggest drop-off: Completed → Recommended (66% loss) — consider reviewing AI scoring thresholds
        </div>
      </div>

      {/* ── SECTION 5: CANDIDATE SIDE PANEL ── */}
      {panelCandidate && (
        <div
          onClick={() => setPanelCandidate(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,.2)',
            zIndex: 200,
            display: 'flex',
            justifyContent: 'flex-end',
            backdropFilter: 'blur(2px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 400,
              height: '100vh',
              background: '#fff',
              display: 'flex',
              flexDirection: 'column',
              borderLeft: `1px solid ${T.bdr}`,
              animation: 'slideIn .22s cubic-bezier(.34,1.56,.64,1)',
            }}
          >
            {/* Head */}
            <div
              style={{
                padding: '18px 20px 14px',
                borderBottom: `1px solid ${T.bdr}`,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                background: T.bg,
                flexShrink: 0,
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: T.t0, letterSpacing: '-.3px' }}>
                  {panelCandidate.candidate}
                </div>
                <div style={{ fontSize: 11, color: T.t3, marginTop: 3 }}>
                  {panelCandidate.campaign} · {panelCandidate.role}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPanelCandidate(null)}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  border: `1px solid ${T.bdr}`,
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: T.t2,
                  fontFamily: 'inherit',
                }}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '18px 20px' }}>
              {(() => {
                const dMins = panelCandidate.duration === '—' ? 0 : parseFloat(panelCandidate.duration) || 1;
                const rate = dMins > 0 ? panelCandidate.violations / dMins : panelCandidate.violations;
                const risk =
                  rate === 0
                    ? {
                        label: 'Low',
                        color: T.green,
                        bg: T.greenLight,
                        reasons: ['Zero violations detected throughout session', 'Stable gaze and consistent engagement'],
                      }
                    : rate < 2
                      ? {
                          label: 'Low',
                          color: T.green,
                          bg: T.greenLight,
                          reasons: ['Very low violation rate', 'Session behaviour was stable'],
                        }
                      : rate < 5
                        ? {
                            label: 'Medium',
                            color: T.amber,
                            bg: T.amberLight,
                            reasons: ['Some gaze deviations detected', 'Minor tab activity noted'],
                          }
                        : rate < 10
                          ? {
                              label: 'High',
                              color: T.red,
                              bg: T.redLight,
                              reasons: ['High violation rate detected', 'Multiple suspicious behaviours flagged'],
                            }
                          : {
                              label: 'Critical',
                              color: '#7F1D1D',
                              bg: T.redLight,
                              reasons: ['Extremely high violation rate', 'Session integrity severely compromised'],
                            };
                const lowConf = dMins < 3 && dMins > 0;
                const sc = Number(panelCandidate.score);
                const scoreC = sc >= 7 ? T.green : sc >= 5 ? T.amber : T.red;
                const recMap = {
                  strong_yes: { label: 'Strong Hire', color: '#065F46', bg: T.greenLight },
                  yes: { label: 'Hire', color: '#1E40AF', bg: T.blueLight },
                  no: { label: 'No Hire', color: '#991B1B', bg: T.redLight },
                };
                const rec = recMap[panelCandidate.recommendation] || { label: 'Pending', color: T.t2, bg: T.bg };
                const comp = [
                  { label: 'Technical', val: sc >= 7 ? sc * 0.95 : sc * 1.1 },
                  { label: 'Communication', val: sc >= 7 ? sc * 0.9 : sc * 0.85 },
                  { label: 'Behavioral', val: sc >= 7 ? sc * 0.88 : sc * 0.8 },
                  {
                    label: 'Integrity',
                    val: panelCandidate.violations === 0 ? 9.5 : Math.max(0, 9.5 - panelCandidate.violations * 0.2),
                  },
                ].map((c) => ({
                  ...c,
                  val: Math.min(10, Math.max(0, parseFloat(c.val.toFixed(1)))),
                }));

                return (
                  <>
                    {/* Score / Risk / Rec */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 18 }}>
                      <div style={{ borderRadius: 12, padding: '14px 14px', background: T.bg, border: `1px solid ${T.bdr}` }}>
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '.9px',
                            color: T.t3,
                            marginBottom: 5,
                          }}
                        >
                          Score
                        </div>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 800,
                            lineHeight: 1,
                            color: scoreC,
                            letterSpacing: '-1px',
                          }}
                        >
                          {sc.toFixed(1)}
                        </div>
                        <div style={{ fontSize: 10, color: T.t3, marginTop: 3 }}>/10</div>
                      </div>
                      <div
                        style={{
                          borderRadius: 12,
                          padding: '14px 14px',
                          background: risk.bg,
                          border: `1px solid ${risk.color}30`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '.9px',
                            color: T.t3,
                            marginBottom: 5,
                          }}
                        >
                          Risk
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: risk.color, lineHeight: 1.2 }}>
                          {risk.label} risk
                        </div>
                        {lowConf && (
                          <div style={{ fontSize: 9, marginTop: 4, color: risk.color, fontWeight: 600 }}>Low confidence</div>
                        )}
                        <div style={{ fontSize: 10, color: risk.color, marginTop: 3, opacity: 0.7 }}>
                          {panelCandidate.duration}
                        </div>
                      </div>
                      <div
                        style={{
                          borderRadius: 12,
                          padding: '14px 14px',
                          background: rec.bg,
                          border: `1px solid ${rec.color}30`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '.9px',
                            color: T.t3,
                            marginBottom: 5,
                          }}
                        >
                          Decision
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: rec.color, lineHeight: 1.2 }}>{rec.label}</div>
                        <div style={{ fontSize: 10, color: rec.color, marginTop: 3, opacity: 0.7 }}>
                          {panelCandidate.started?.split(' ')[0] || '—'}
                        </div>
                      </div>
                    </div>

                    {/* Risk Explanation */}
                    <div style={{ marginBottom: 14 }}>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '.9px',
                          color: T.t3,
                          marginBottom: 8,
                        }}
                      >
                        Risk explanation
                      </div>
                      {risk.reasons.map((r, i) => (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            gap: 7,
                            fontSize: 11,
                            color: T.t2,
                            lineHeight: 1.55,
                            marginBottom: 5,
                            fontWeight: 500,
                          }}
                        >
                          <div
                            style={{
                              width: 5,
                              height: 5,
                              borderRadius: '50%',
                              background: risk.color,
                              marginTop: 6,
                              flexShrink: 0,
                            }}
                          />
                          {r}
                        </div>
                      ))}
                      <div style={{ fontSize: 10, color: T.t3, marginTop: 4 }}>
                        Rate: {rate.toFixed(1)} violations/min
                        {lowConf && ' — low confidence (short session)'}
                      </div>
                    </div>

                    <div style={{ height: 1, background: T.bdr, margin: '12px 0' }} />

                    {/* Violation Breakdown */}
                    <div style={{ marginBottom: 14 }}>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '.9px',
                          color: T.t3,
                          marginBottom: 8,
                        }}
                      >
                        Violation breakdown
                      </div>
                      {[
                        ['Tab switch', Math.round(panelCandidate.violations * 0.2)],
                        ['Looking away', Math.round(panelCandidate.violations * 0.5)],
                        ['Multiple faces', Math.round(panelCandidate.violations * 0.05)],
                        ['Window lost focus', Math.round(panelCandidate.violations * 0.25)],
                      ].map(([t, v]) => (
                        <div
                          key={t}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: 11,
                            color: T.t2,
                            padding: '5px 0',
                            borderBottom: `1px solid ${T.bg}`,
                            fontWeight: 500,
                          }}
                        >
                          <span>{t}</span>
                          <span style={{ fontWeight: 700, color: T.t0 }}>{v}</span>
                        </div>
                      ))}
                    </div>

                    <div style={{ height: 1, background: T.bdr, margin: '12px 0' }} />

                    {/* Competency Breakdown */}
                    <div style={{ marginBottom: 14 }}>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '.9px',
                          color: T.t3,
                          marginBottom: 10,
                        }}
                      >
                        Competency breakdown
                      </div>
                      {comp.map(({ label, val }) => {
                        const cc = val >= 7 ? T.green : val >= 5 ? T.amber : T.red;
                        return (
                          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <div style={{ fontSize: 10, color: T.t2, width: 96, flexShrink: 0, fontWeight: 500 }}>
                              {label}
                            </div>
                            <div style={{ flex: 1, height: 5, background: T.bg, borderRadius: 3, overflow: 'hidden' }}>
                              <div
                                style={{
                                  height: '100%',
                                  borderRadius: 3,
                                  background: cc,
                                  width: `${val * 10}%`,
                                  transition: 'width .8s ease',
                                }}
                              />
                            </div>
                            <div
                              style={{ fontSize: 11, fontWeight: 700, color: cc, width: 28, textAlign: 'right', flexShrink: 0 }}
                            >
                              {val.toFixed(1)}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ height: 1, background: T.bdr, margin: '12px 0' }} />

                    {/* AI Summary */}
                    <div style={{ marginBottom: 16 }}>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '.9px',
                          color: T.t3,
                          marginBottom: 8,
                        }}
                      >
                        AI summary
                      </div>
                      <div style={{ background: T.bg, borderRadius: 10, padding: '12px 14px', fontSize: 11, lineHeight: 1.65 }}>
                        {sc >= 7 ? (
                          <>
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '.8px',
                                color: T.green,
                                marginBottom: 4,
                              }}
                            >
                              Strengths
                            </div>
                            <div style={{ color: T.green, marginBottom: 2 }}>+ Strong technical depth and clear communication</div>
                            <div style={{ color: T.green, marginBottom: 2 }}>+ Provided specific real-world examples throughout</div>
                            {panelCandidate.violations === 0 && (
                              <div style={{ color: T.green }}>+ Zero violations — high integrity session</div>
                            )}
                          </>
                        ) : sc >= 5 ? (
                          <>
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '.8px',
                                color: T.amber,
                                marginBottom: 4,
                              }}
                            >
                              Strengths
                            </div>
                            <div style={{ color: T.amber, marginBottom: 6 }}>+ Solid foundational knowledge demonstrated</div>
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '.8px',
                                color: '#92400E',
                                marginBottom: 4,
                              }}
                            >
                              Concerns
                            </div>
                            <div style={{ color: '#92400E' }}>− Answers lacked depth in advanced topics</div>
                          </>
                        ) : (
                          <>
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '.8px',
                                color: T.red,
                                marginBottom: 4,
                              }}
                            >
                              Concerns
                            </div>
                            <div style={{ color: T.red, marginBottom: 2 }}>− No substantive answers provided to technical questions</div>
                            <div style={{ color: T.red, marginBottom: 2 }}>− Near-zero scores across all technical dimensions</div>
                            {panelCandidate.violations > 10 && (
                              <div style={{ color: T.red }}>− {panelCandidate.violations} proctoring violations flagged</div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <button
                        type="button"
                        style={{
                          padding: '10px',
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                          background: T.greenLight,
                          color: '#065F46',
                          border: '1px solid #86EFAC',
                          fontFamily: 'inherit',
                        }}
                      >
                        ✓ Shortlist
                      </button>
                      <button
                        type="button"
                        style={{
                          padding: '10px',
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                          background: T.redLight,
                          color: '#991B1B',
                          border: '1px solid #FECACA',
                          fontFamily: 'inherit',
                        }}
                      >
                        ✕ Reject
                      </button>
                      <button
                        type="button"
                        style={{
                          padding: '10px',
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                          background: '#fff',
                          color: T.t2,
                          border: `1px solid ${T.bdr}`,
                          gridColumn: '1 / -1',
                          fontFamily: 'inherit',
                        }}
                      >
                        ▶ View recording
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/admin/reports')}
                        style={{
                          padding: '10px',
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                          background: T.blueLight,
                          color: '#1E40AF',
                          border: '1px solid #BFDBFE',
                          gridColumn: '1 / -1',
                          fontFamily: 'inherit',
                        }}
                      >
                        ■ Full report
                      </button>
                    </div>
                  </>
                );
              })()}
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

